import { useMemo } from 'react';
import { useQueries } from 'react-query';
import dayjs from 'dayjs';

import { checkoutQuery, useBranches, useCheckout, useProducts, useReports } from '../../hooks';
import { DATE_FORMAT, enumerateDays, getPreviousRange, getToday } from '../../utils';
import { getStockStatus } from '../../components';

const MAX_BRANCH_BREAKDOWN = 30;

const sumValues = map => Object.values(map).reduce((acc, v) => acc + v, 0);

const ratioDelta = (current, previous) => {
  if (!previous) return null;
  return (current - previous) / previous;
};

const revenueByDate = rows => {
  const map = {};
  (Array.isArray(rows) ? rows : []).forEach(row => {
    map[row.report_date] = (map[row.report_date] ?? 0) + (Number(row.total_amount) || 0);
  });
  return map;
};

const aggregateReports = (data, productFilter) => {
  const unitsByDate = {};
  const profitByDate = {};
  const allProfitByDate = {};
  const products = {};
  if (data && typeof data === 'object') {
    Object.entries(data).forEach(([date, rows]) => {
      rows.forEach(row => {
        const units = Number(row.total_quantity) || 0;
        const profit = Number(row.total_profit) || 0;
        allProfitByDate[date] = (allProfitByDate[date] ?? 0) + profit;
        if (productFilter && !productFilter.has(row.product_id)) return;
        unitsByDate[date] = (unitsByDate[date] ?? 0) + units;
        profitByDate[date] = (profitByDate[date] ?? 0) + profit;
        const item = products[row.product_id] ?? { id: row.product_id, name: row.name, units: 0, profit: 0 };
        item.units += units;
        item.profit += profit;
        products[row.product_id] = item;
      });
    });
  }
  return { unitsByDate, profitByDate, allProfitByDate, products: Object.values(products) };
};

export const useDashboardData = ({ range, branchId, productIds, isAdmin }) => {
  const prevRange = useMemo(() => getPreviousRange(range), [range]);
  const params = { branch_id: branchId, start_date: range[0], end_date: range[1] };
  const prevParams = { branch_id: branchId, start_date: prevRange[0], end_date: prevRange[1] };
  const today = getToday();
  const yesterday = dayjs().subtract(1, 'day').format(DATE_FORMAT);

  const keep = { keepPreviousData: true };
  const checkout = useCheckout(params, keep);
  const checkoutPrev = useCheckout(prevParams, keep);
  const reports = useReports(params, keep);
  const reportsPrev = useReports(prevParams, keep);
  const todayQuery = useCheckout({ branch_id: branchId, start_date: yesterday, end_date: today }, keep);
  const branches = useBranches();
  const products = useProducts();

  const branchList = useMemo(() => (Array.isArray(branches.data) ? branches.data : []), [branches.data]);
  const breakdownEnabled = branchId === 0 && branchList.length > 1 && branchList.length <= MAX_BRANCH_BREAKDOWN;

  const breakdownQueries = useQueries(
    (breakdownEnabled ? branchList : []).map(branch => ({
      ...checkoutQuery({ branch_id: branch.id, start_date: range[0], end_date: range[1] }),
      keepPreviousData: true,
    })),
  );

  const productFilter = useMemo(() => (productIds?.length ? new Set(productIds) : null), [productIds]);

  const days = useMemo(() => enumerateDays(range), [range]);
  const prevDays = useMemo(() => enumerateDays(prevRange), [prevRange]);

  const revenue = useMemo(() => revenueByDate(checkout.data), [checkout.data]);
  const revenuePrev = useMemo(() => revenueByDate(checkoutPrev.data), [checkoutPrev.data]);
  const current = useMemo(() => aggregateReports(reports.data, productFilter), [reports.data, productFilter]);
  const previous = useMemo(
    () => aggregateReports(reportsPrev.data, productFilter),
    [reportsPrev.data, productFilter],
  );

  const breakdownLoading = breakdownQueries.some(q => q.isLoading);
  const breakdownError = breakdownQueries.some(q => q.isError);
  const breakdownData = breakdownQueries.map(q => q.data);

  const branchRevenue = useMemo(() => {
    if (!breakdownEnabled) return [];
    return branchList
      .map((branch, i) => ({
        id: branch.id,
        name: branch.name,
        value: sumValues(revenueByDate(breakdownData[i])),
      }))
      .sort((a, b) => b.value - a.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdownEnabled, branchList, JSON.stringify(breakdownData)]);

  const kpis = useMemo(() => {
    const totalRevenue = sumValues(revenue);
    const totalRevenuePrev = sumValues(revenuePrev);
    const units = sumValues(current.unitsByDate);
    const unitsPrev = sumValues(previous.unitsByDate);
    const profit = sumValues(current.profitByDate);
    const profitPrev = sumValues(previous.profitByDate);
    const allProfit = sumValues(current.allProfitByDate);
    const todayRows = revenueByDate(todayQuery.data);
    const salesDays = days.filter(d => (revenue[d] ?? 0) > 0).length;

    const productList = Array.isArray(products.data) ? products.data : [];
    const hasStock = productList.some(p => p.quantity !== undefined);
    const stockUnits = productList.reduce((acc, p) => acc + (Number(p.quantity) || 0), 0);
    const lowStock = productList
      .filter(p => p.quantity !== undefined && getStockStatus(p.quantity) !== 'ok')
      .sort((a, b) => a.quantity - b.quantity);

    let activeBranches;
    let totalBranches;
    if (branchId === 0) {
      totalBranches = branchList.length;
      activeBranches = breakdownEnabled
        ? branchRevenue.filter(b => b.value > 0).length
        : totalRevenue > 0
        ? branchList.length
        : 0;
    } else {
      totalBranches = 1;
      activeBranches = totalRevenue > 0 ? 1 : 0;
    }

    return {
      revenue: totalRevenue,
      revenueDelta: ratioDelta(totalRevenue, totalRevenuePrev),
      today: todayRows[today] ?? 0,
      todayDelta: ratioDelta(todayRows[today] ?? 0, todayRows[yesterday] ?? 0),
      units,
      unitsDelta: ratioDelta(units, unitsPrev),
      profit,
      profitDelta: ratioDelta(profit, profitPrev),
      margin: !productFilter && totalRevenue > 0 ? allProfit / totalRevenue : null,
      avgDaily: days.length ? totalRevenue / days.length : 0,
      salesDays,
      hasStock,
      stockUnits,
      skuCount: productList.length,
      lowStock,
      activeBranches,
      totalBranches,
    };
  }, [
    revenue,
    revenuePrev,
    current,
    previous,
    todayQuery.data,
    days,
    products.data,
    branchId,
    branchList,
    breakdownEnabled,
    branchRevenue,
    productFilter,
    today,
    yesterday,
  ]);

  const series = useMemo(() => {
    const build = (map, prevMap) =>
      days.map((date, i) => ({
        date,
        value: map[date] ?? 0,
        prev: prevMap[prevDays[i]] ?? 0,
        prevDate: prevDays[i],
      }));
    return {
      revenue: build(revenue, revenuePrev),
      units: build(current.unitsByDate, previous.unitsByDate),
      profit: build(current.profitByDate, previous.profitByDate),
    };
  }, [days, prevDays, revenue, revenuePrev, current, previous]);

  const sparks = useMemo(
    () => ({
      revenue: series.revenue.map(d => ({ v: d.value })),
      units: series.units.map(d => ({ v: d.value })),
      profit: series.profit.map(d => ({ v: d.value })),
    }),
    [series],
  );

  const revenueVsCost = useMemo(
    () =>
      days.map(date => {
        const rev = revenue[date] ?? 0;
        const profit = current.allProfitByDate[date] ?? 0;
        return { date, cost: Math.max(0, rev - profit), profit };
      }),
    [days, revenue, current.allProfitByDate],
  );

  const topProducts = useMemo(
    () =>
      [...current.products]
        .filter(p => p.units > 0)
        .sort((a, b) => b.units - a.units)
        .slice(0, 5)
        .map(p => ({ name: p.name, value: p.units, profit: p.profit })),
    [current.products],
  );

  const productShare = useMemo(() => {
    const sorted = [...current.products].filter(p => p.units > 0).sort((a, b) => b.units - a.units);
    const head = sorted.slice(0, 5).map(p => ({ name: p.name, value: p.units }));
    const rest = sorted.slice(5).reduce((acc, p) => acc + p.units, 0);
    return rest > 0 ? [...head, { name: '__others__', value: rest }] : head;
  }, [current.products]);

  const dailyRows = useMemo(
    () =>
      [...days]
        .reverse()
        .filter(date => (revenue[date] ?? 0) > 0 || (current.unitsByDate[date] ?? 0) > 0)
        .map(date => ({
          id: date,
          date,
          revenue: revenue[date] ?? 0,
          units: current.unitsByDate[date] ?? 0,
          profit: current.profitByDate[date] ?? 0,
        })),
    [days, revenue, current],
  );

  const refetchAll = () => {
    checkout.refetch();
    checkoutPrev.refetch();
    reports.refetch();
    reportsPrev.refetch();
    todayQuery.refetch();
    products.refetch();
    breakdownQueries.forEach(q => q.refetch());
  };

  return {
    kpis,
    series,
    sparks,
    revenueVsCost,
    topProducts,
    productShare,
    dailyRows,
    branchRevenue,
    branches: branchList,
    products: Array.isArray(products.data) ? products.data : [],
    breakdownEnabled,
    refetchAll,
    status: {
      revenue: { loading: checkout.isLoading, error: checkout.isError, refetch: checkout.refetch },
      revenuePrev: { loading: checkoutPrev.isLoading },
      reports: { loading: reports.isLoading, error: reports.isError, refetch: reports.refetch },
      reportsPrev: { loading: reportsPrev.isLoading },
      today: { loading: todayQuery.isLoading, error: todayQuery.isError },
      products: { loading: products.isLoading, error: products.isError, refetch: products.refetch },
      branches: { loading: branches.isLoading },
      breakdown: {
        loading: breakdownLoading || branches.isLoading,
        error: breakdownError,
        refetch: () => breakdownQueries.forEach(q => q.refetch()),
      },
      fetching: checkout.isFetching || reports.isFetching,
    },
  };
};
